/**
 * Today's HELM card, rendered on the Today tab in the configured card style.
 *
 * Layout (A7Y-4407, TPF round 5): the card is a stack of separate section
 * cards, each with its own `Subheading` and, where the section carries
 * items, its own Catalyst `Table` — Critical first, then Due today (every
 * roster row due today, A7Y-4733) and Overdue (A7Y-5873), then Shipped beside Meetings, Inbox beside
 * Judgment queue, then Slack, the model's own body lines ("Notes") and the
 * could-not-verify notice. The page title is the
 * card's date as an `h2`; every section title is an `h3`.
 *
 * Content is never re-derived. `scripts/helm/cards.cjs` already tags every
 * line with its `section` and writes each section as one summary line (its
 * "label", `Title: rest`) followed by that section's own item lines, in a
 * fixed order, with fixed ` — ` separators. The label's title becomes the
 * section heading and its rest the section's summary; item lines are split
 * into columns on those same separators. Meetings, whose label line carries
 * one key per event, render one row per event from `facts.meetings` — the
 * same title and Madrid clock time cards.cjs printed. Anything that does
 * not match its section's known shape falls back to the line's own text.
 *
 * A row whose item key is in `suppressedKeys` — the server's own
 * `isHelmItemSuppressed` verdict, carried on `overview.suppressedKeys` — is
 * hidden: optimistically the moment an action fires (`useHelmActions` adds
 * the key to the cached set), and for real once the next run's overlay
 * agrees. The server recomputes `suppressedKeys` from the live date, so an
 * expired Defer/Waiting-on shows the row again; this component never
 * re-derives that itself.
 *
 * `onAction` is optional: without it the card renders read-only, with no
 * action menus.
 *
 * `record` is the record as it is now (A7Y-5492): the open roster items, read
 * through the same query This week reads them with. With it, a Critical
 * pipeline row shows only while its item is still open and due today or
 * earlier, and Due today lists the record's items due today (`rosterDueOn`,
 * This week's own filter) rather than the morning run's — so a row closed or
 * re-dated at noon leaves both at once. Without it (a past run, or the record
 * unreadable) both sections show the run's rows, labelled as of the run. A
 * trigger whose fallback has passed is overdue, not due (`rosterDue`), and a
 * row whose HELM proposal Inbox lists under Refused is not due until HELM
 * proposes it again (`refusedRosterIds`, A7Y-5873).
 *
 * Type badges come from the item key's prefix (`helmItemKey`'s own scheme,
 * packages/platform-health/src/helm/keys.ts), never from the text; critical
 * reasons come from `facts.critical[].reasons`, and the ` — reason, reason`
 * suffix cards.cjs appended to the line is removed from the Item cell only
 * on an exact match against that same array.
 *
 * Every card line maps to exactly one `data-testid="helm-card-line"`
 * element (the title, each section header, each item row), so a test can
 * prove no line was dropped; per-event meeting rows carry
 * `helm-card-meeting` instead, since they expand one line.
 */
import { useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Badge,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Heading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type {
  HelmActionInput,
  HelmCard,
  HelmCardLine,
  HelmCommitment,
  HelmDecision,
  HelmFacts,
} from '@a7y/platform-health/helm';
import type { CrmRosterItemDoc } from '@a7y/types';
import { buildHelmDecisionIndex, buildHelmLinkIndex, firstLinkForKeys } from '../links';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';
import gmailIcon from '../icons/gmail.svg';
import linearIcon from '../icons/linear.svg';
import slackIcon from '../icons/slack.png';
import { HelmLink } from '../HelmLink';
import {
  HELM_JUDGMENT_STATE_LIST,
  HELM_SHIPPED_ASKS,
  helmCommitmentMarkdown,
  helmRowSummary,
  helmSlackText,
} from '@a7y/platform-health/helm';
import { RowMore } from '../../crm/CrmMarkdown';
import { rosterDue, rosterDueOn, rosterOverdue } from '../../crm/accountView';
import { shortDate } from '../../crm/board';
import { accountPagePath } from '../../crm/links';
import { HELM_WEEK_PATH } from '../inAppPath';
import { refusedRosterIds } from '../staleRows';

type BadgeColor = NonNullable<ComponentProps<typeof Badge>['color']>;
type Section = HelmCardLine['section'];

/** The record as it is now (A7Y-5492): its open roster items and today's date. */
export interface HelmLiveRecord {
  items: CrmRosterItemDoc[];
  today: string;
}

interface CardContext {
  facts: HelmFacts;
  linkIndex: Map<string, string | null>;
  decisionIndex: Map<string, HelmDecision | null> | null;
  suppressedKeys: ReadonlySet<string>;
  /** Record rows whose HELM proposal Inbox lists under Refused, not proposed again (A7Y-5873): not due. */
  refusedIds: ReadonlySet<string>;
  onAction?: (input: HelmActionInput) => void;
  record: {
    today: string;
    items: CrmRosterItemDoc[];
    openById: Map<string, CrmRosterItemDoc>;
  } | null;
}

/* ─── Line shapes ──────────────────────────────────────────────────────── */

type CardGroup = { section: Section; label: HelmCardLine | null; rows: HelmCardLine[] };

/** Sections whose every line is an item — no summary line leads them. */
const LABELLESS = new Set<Section>(['body']);

/** Adjacent-equal split on `line.section` — reproduces cards.cjs's own
 * grouping without re-deriving it. */
function groupCardLines(lines: readonly HelmCardLine[]): CardGroup[] {
  const groups: CardGroup[] = [];
  for (const line of lines) {
    const last = groups[groups.length - 1];
    if (last && last.section === line.section) {
      last.rows.push(line);
    } else if (LABELLESS.has(line.section)) {
      groups.push({ section: line.section, label: null, rows: [line] });
    } else {
      groups.push({ section: line.section, label: line, rows: [] });
    }
  }
  return groups;
}

/** `Meetings today: A · B` → `{ title: 'Meetings today', rest: 'A · B' }`. */
function splitLabel(text: string): { title: string; rest: string } {
  const i = text.indexOf(':');
  if (i < 0) return { title: text, rest: '' };
  return { title: text.slice(0, i), rest: text.slice(i + 1).trim() };
}

const BULLET = /^·\s*/;
const SEP = ' — ';

function withoutBullet(text: string): string {
  return text.replace(BULLET, '');
}

/** The full text of the roster row a line names, behind "Show more" (A7Y-5342): the line carries
 * only the ask and one line of why. Null for a line that is not a roster row. */
function RowMoreFor({ ctx, itemKey }: { ctx: CardContext; itemKey: string | null }) {
  const commitment = itemKey ? ctx.facts.commitments.find((c) => c.key === itemKey) : undefined;
  if (!commitment) return null;
  return <RowMore markdown={helmCommitmentMarkdown(commitment)} testId="helm-card-row-more" />;
}

/**
 * A pipeline row's text from its own record text (A7Y-5492): what it is about and
 * its next action, by the one rule the card and every page use. Read here rather
 * than from the card line so a run written before the rule changed still reads
 * right. Null for a line that is not a roster row.
 */
function rosterRowText(ctx: CardContext, itemKey: string | null): string | null {
  const commitment = commitmentFor(ctx, itemKey);
  if (!commitment) return null;
  const { ask, next } = helmRowSummary(helmCommitmentMarkdown(commitment), {
    decision: commitment.table === 'scheduled_decisions',
  });
  return next ? `${ask} — ${next}` : ask;
}

/** Critical item text without the ` — reason, reason` suffix, only when that
 * exact suffix (built from the same structured reasons) is present. */
function criticalItemText(text: string, reasons: readonly string[]): string {
  const t = withoutBullet(text);
  if (reasons.length === 0) return t;
  const suffix = `${SEP}${reasons.join(', ')}`;
  return t.endsWith(suffix) ? t.slice(0, -suffix.length) : t;
}

/** The trailing ` · <status>` parts a shipped line can carry: what it asks of Yevhen (A7Y-5873); older runs' two. */
const SHIPPED_STATUSES = new Set<string>([
  ...Object.values(HELM_SHIPPED_ASKS),
  'your review pending',
  'in your roster',
]);
const isShippedStatus = (part: string) =>
  SHIPPED_STATUSES.has(part) || part.startsWith('carried since ');

/** `· A7Y-1 — Title · carried since … · your re-check` → id / title / statuses. */
function parseShipped(text: string): { id: string; title: string; statuses: string[] } | null {
  const t = withoutBullet(text);
  const i = t.indexOf(SEP);
  if (i < 0) return null;
  const parts = t.slice(i + SEP.length).split(' · ');
  const statuses: string[] = [];
  while (parts.length > 1 && isShippedStatus(parts[parts.length - 1]))
    statuses.unshift(parts.pop()!);
  return { id: t.slice(0, i), title: parts.join(' · '), statuses };
}

/** `· who — where — message — when`; the message itself may contain ` — `. */
function parseSlack(
  text: string
): { who: string; where: string; message: string; when: string } | null {
  const parts = withoutBullet(text).split(SEP);
  if (parts.length < 4) return null;
  return {
    who: parts[0],
    where: parts[1],
    message: parts.slice(2, -1).join(SEP),
    when: parts[parts.length - 1],
  };
}

/* ─── Type and reason badges ───────────────────────────────────────────── */

/** `gmail:li:` is checked before the bare `gmail:` it would otherwise match
 * (LinkedIn mail is keyed under the `gmail:` namespace). */
const HELM_TYPE_BY_PREFIX: { prefix: string; label: string; color: BadgeColor }[] = [
  { prefix: 'gmail:li:', label: 'LinkedIn', color: 'sky' },
  { prefix: 'gmail:', label: 'Email', color: 'blue' },
  { prefix: 'slack:', label: 'Slack', color: 'fuchsia' },
  { prefix: 'linear:', label: 'Linear Ticket', color: 'indigo' },
  { prefix: 'jq:', label: 'Linear Ticket', color: 'indigo' },
  { prefix: 'deployed:', label: 'Shipped', color: 'teal' },
  { prefix: 'roster:', label: 'Pipeline', color: 'amber' },
  { prefix: 'cal:', label: 'Meeting', color: 'emerald' },
  { prefix: 'git:', label: 'Commit', color: 'zinc' },
  { prefix: 'drive:', label: 'Call Note', color: 'cyan' },
  { prefix: 'sender:', label: 'Email', color: 'blue' },
  { prefix: 'capture:', label: 'Note', color: 'lime' },
  { prefix: 'proposal:', label: 'Proposal', color: 'violet' },
];

function helmTypeFor(key: string | null): { label: string; color: BadgeColor } {
  if (key) {
    const match = HELM_TYPE_BY_PREFIX.find((t) => key.startsWith(t.prefix));
    if (match) return match;
  }
  return { label: 'Other', color: 'zinc' };
}

/** The source's own favicon where there is one (Linear, Gmail, Slack), plain
 * text otherwise (TPF, A7Y-4407). Bundled, never hot-linked. */
const TYPE_ICON: Record<string, string> = {
  'Linear Ticket': linearIcon,
  Email: gmailIcon,
  Slack: slackIcon,
};

function TypeLabel({ itemKey }: { itemKey: string | null }) {
  const { label } = helmTypeFor(itemKey);
  const icon = TYPE_ICON[label];
  if (icon)
    return <img src={icon} alt={label} title={label} className="size-5 shrink-0 rounded-sm" />;
  return <span className="text-zinc-500 dark:text-zinc-400">{label}</span>;
}

/** The four reasons a row can be Critical (A7Y-4733). An older run doc may
 * still carry a retired reason (`money`, `deal_within_7d`, `deployed`); it
 * renders zinc via the `?? 'zinc'` below rather than failing. */
const REASON_COLOR: Record<string, BadgeColor> = {
  live_customer: 'purple',
  person_waiting: 'blue',
  hard_deadline: 'red',
};

function ReasonBadges({ reasons, className }: { reasons: readonly string[]; className?: string }) {
  if (reasons.length === 0) return null;
  return (
    <div className={['flex flex-wrap gap-1', className].filter(Boolean).join(' ')}>
      {reasons.map((reason) => (
        <Badge key={reason} color={REASON_COLOR[reason] ?? 'zinc'}>
          {reason.replace(/_/g, ' ')}
        </Badge>
      ))}
    </div>
  );
}

/* ─── Building blocks ──────────────────────────────────────────────────── */

const MADRID_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Madrid',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function EvidenceLink({
  ctx,
  keys,
  children,
}: {
  ctx: CardContext;
  keys: readonly string[];
  children: ReactNode;
}) {
  const url = firstLinkForKeys(ctx.linkIndex, keys);
  if (!url) return <>{children}</>;
  return (
    <HelmLink href={url} className="hover:underline">
      {children}
    </HelmLink>
  );
}

function ActionCell({ ctx, itemKey }: { ctx: CardContext; itemKey: string | null }) {
  if (!ctx.onAction) return null;
  const decision = itemKey && ctx.decisionIndex ? (ctx.decisionIndex.get(itemKey) ?? null) : null;
  return (
    <TableCell className="w-12 text-right">
      {itemKey && <ItemActionsMenu itemKey={itemKey} decision={decision} onAction={ctx.onAction} />}
    </TableCell>
  );
}

function ActionHeader({ ctx }: { ctx: CardContext }) {
  if (!ctx.onAction) return null;
  return (
    <TableHeader className="w-12">
      <span className="sr-only">Actions</span>
    </TableHeader>
  );
}

/** The run's roster row a key names, or undefined for any other item. */
function commitmentFor(ctx: CardContext, key: string | null): HelmCommitment | undefined {
  return key ? ctx.facts.commitments.find((c) => c.key === key) : undefined;
}

/**
 * Whether a run's pipeline row still holds in the record now (A7Y-5492): its item
 * is open and due today or earlier. Any other row, or no record to read, keeps
 * the run's verdict.
 */
function holdsInRecord(ctx: CardContext, key: string | null): boolean {
  const commitment = commitmentFor(ctx, key);
  if (!ctx.record || !commitment) return true;
  const item = ctx.record.openById.get(commitment.evidence.ref.id);
  return item !== undefined && item.due <= ctx.record.today;
}

function visibleRows(rows: readonly HelmCardLine[], ctx: CardContext): HelmCardLine[] {
  return rows.filter((line) => {
    const key = line.keys[0] ?? null;
    return !(key && ctx.suppressedKeys.has(key));
  });
}

/* ─── Sections ─────────────────────────────────────────────────────────── */

function CardTitle({ line }: { line: HelmCardLine }) {
  // The page is already HELM; its card title opens with the same word (TPF, A7Y-4407).
  const text = line.text.replace(/^HELM\s*·\s*/, '');
  const i = text.lastIndexOf(' · ');
  const date = i < 0 ? text : text.slice(0, i);
  const dayType = i < 0 ? '' : text.slice(i + 3);
  return (
    <div data-testid="helm-card-line" className="flex flex-wrap items-center gap-3">
      <Heading level={2}>{date}</Heading>
      {dayType && <Badge color="zinc">{dayType}</Badge>}
    </div>
  );
}

function CriticalSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  // The count is not shown (TPF, A7Y-4407): the rows are the count.
  const { title } = splitLabel(group.label!.text);
  const rows = visibleRows(group.rows, ctx).filter((line) =>
    holdsInRecord(ctx, line.keys[0] ?? null)
  );
  return (
    <HelmSection title={title} critical>
      {rows.length === 0 ? (
        <HelmEmpty>None</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader className="hidden w-12 sm:table-cell">Type</TableHeader>
              <TableHeader>Item</TableHeader>
              <TableHeader className="hidden sm:table-cell">Why</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line, i) => {
              const key = line.keys[0] ?? null;
              const reasons = ctx.facts.critical.find((c) => c.key === key)?.reasons ?? [];
              return (
                <TableRow key={`${key ?? 'line'}-${i}`} data-testid="helm-card-line">
                  <TableCell className="hidden sm:table-cell">
                    <TypeLabel itemKey={key} />
                  </TableCell>
                  <TableCell className="whitespace-normal sm:min-w-56">
                    <div className="mb-1 sm:hidden">
                      <TypeLabel itemKey={key} />
                    </div>
                    <EvidenceLink ctx={ctx} keys={line.keys}>
                      {rosterRowText(ctx, key) ?? criticalItemText(line.text, reasons)}
                    </EvidenceLink>
                    <RowMoreFor ctx={ctx} itemKey={key} />
                    <ReasonBadges reasons={reasons} className="mt-1.5 sm:hidden" />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <ReasonBadges reasons={reasons} />
                  </TableCell>
                  <ActionCell ctx={ctx} itemKey={key} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

/** `roster_close` → `roster close`; the commitments table name, de-slugged. */
function tableLabel(table: string): string {
  return table.replace(/_/g, ' ');
}

/** A record item's kind as the table name the run's rows carry. */
const TABLE_BY_KIND: Record<CrmRosterItemDoc['kind'], string> = {
  touch: 'prospects',
  'follow-up': 'follow_ups',
  decision: 'scheduled_decisions',
  trigger: 'scheduled_decisions',
};

/** A record item's subject linked to its page, its next action, and the rest behind "Show more". */
function LiveRowText({ item, href }: { item: CrmRosterItemDoc; href: string }) {
  const { ask, next } = helmRowSummary(item.title, {
    decision: item.kind === 'decision' || item.kind === 'trigger',
  });
  return (
    <span data-testid="helm-due-row">
      <HelmLink href={href} className="font-medium hover:underline">
        {ask}
      </HelmLink>
      {next ? <span className="text-zinc-600 dark:text-zinc-400"> — {next}</span> : null}
      <RowMore markdown={item.title} testId="helm-card-row-more" />
    </span>
  );
}

/**
 * Due today from the record as it is now (A7Y-5492): every open item due today
 * (`rosterDueOn`, This week's own filter), so the header count is This week's
 * Today count. A row already listed under Critical is counted here and listed
 * there. A row the run also holds keeps its HELM key, so it keeps its actions.
 */
function LiveDueSection({ title, ctx }: { title: string; ctx: CardContext }) {
  const record = ctx.record!;
  const listed = rosterDueOn(record.items, record.today);
  // A7Y-5873: a row whose HELM proposal Inbox refused is not due until HELM proposes it again.
  const due = listed.filter((item) => !ctx.refusedIds.has(item.id));
  const held = listed.length - due.length;
  const criticalIds = new Set(
    ctx.facts.critical
      .map((c) => commitmentFor(ctx, c.key))
      .filter((c): c is HelmCommitment => c !== undefined && !ctx.suppressedKeys.has(c.key))
      .map((c) => c.evidence.ref.id)
  );
  const rows = due
    .filter((item) => !criticalIds.has(item.id))
    .map((item) => ({
      item,
      key:
        ctx.facts.commitments.find((c) => c.evidence.ref.id === item.id && c.nextTouch === item.due)
          ?.key ?? null,
    }))
    .filter(({ key }) => !(key && ctx.suppressedKeys.has(key)));
  const underCritical = due.length - due.filter((item) => !criticalIds.has(item.id)).length;
  const summary = [
    underCritical > 0 ? `${underCritical} of them under Critical` : null,
    held > 0
      ? `${held} not counted: HELM's proposal on ${held === 1 ? 'it' : 'them'} is under Refused on the Inbox tab`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <HelmSection
      title={title}
      count={due.length}
      summary={summary || undefined}
      lineOn={due.length > 0 ? 'header' : 'section'}
    >
      {due.length === 0 ? (
        <HelmEmpty>None</HelmEmpty>
      ) : rows.length === 0 ? null : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader className="hidden w-12 sm:table-cell">Type</TableHeader>
              <TableHeader>Item</TableHeader>
              <TableHeader className="hidden sm:table-cell">Table · block</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ item, key }) => {
              const type = helmTypeFor('roster:');
              const where = [tableLabel(TABLE_BY_KIND[item.kind]), item.block]
                .filter(Boolean)
                .join(' · ');
              const href = item.accountId ? accountPagePath(item.accountId) : HELM_WEEK_PATH;
              return (
                <TableRow key={item.id} data-testid="helm-card-line">
                  <TableCell className="hidden sm:table-cell">
                    <Badge color={type.color}>{type.label}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-normal sm:min-w-56">
                    <div className="mb-1 sm:hidden">
                      <Badge color={type.color}>{type.label}</Badge>
                    </div>
                    <LiveRowText item={item} href={href} />
                    <div className="mt-1 text-zinc-500 sm:hidden dark:text-zinc-400">{where}</div>
                  </TableCell>
                  <TableCell className="hidden text-zinc-500 sm:table-cell dark:text-zinc-400">
                    {where}
                  </TableCell>
                  <ActionCell ctx={ctx} itemKey={key} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

/** Every actionable roster row due today that is not already Critical
 * (A7Y-4733). The item text is the card line's own; the Table · block cell
 * reads the structured commitment the line's key names, never the text. With
 * the record read, the record's own rows replace the run's (A7Y-5492). */
function DueSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const { title, rest } = splitLabel(group.label!.text);
  if (ctx.record) return <LiveDueSection title={title} ctx={ctx} />;
  const rows = visibleRows(group.rows, ctx).filter((line) => {
    const commitment = commitmentFor(ctx, line.keys[0] ?? null);
    return !(commitment && ctx.refusedIds.has(commitment.evidence.ref.id));
  });
  return (
    <HelmSection
      title={title}
      count={group.rows.length > 0 ? rows.length : undefined}
      lineOn={group.rows.length > 0 ? 'header' : 'section'}
    >
      {group.rows.length === 0 ? (
        <HelmEmpty>{rest || 'None'}</HelmEmpty>
      ) : rows.length === 0 ? null : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader className="hidden w-12 sm:table-cell">Type</TableHeader>
              <TableHeader>Item</TableHeader>
              <TableHeader className="hidden sm:table-cell">Table · block</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line, i) => {
              const key = line.keys[0] ?? null;
              const commitment = ctx.facts.commitments.find((c) => c.key === key);
              const where = commitment
                ? [tableLabel(commitment.table), commitment.block].filter(Boolean).join(' · ')
                : '';
              const type = helmTypeFor(key);
              return (
                <TableRow key={`${key ?? 'line'}-${i}`} data-testid="helm-card-line">
                  <TableCell className="hidden sm:table-cell">
                    <Badge color={type.color}>{type.label}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-normal sm:min-w-56">
                    <div className="mb-1 sm:hidden">
                      <Badge color={type.color}>{type.label}</Badge>
                    </div>
                    <EvidenceLink ctx={ctx} keys={line.keys}>
                      {rosterRowText(ctx, key) ?? withoutBullet(line.text)}
                    </EvidenceLink>
                    <RowMoreFor ctx={ctx} itemKey={key} />
                    {where && (
                      <div className="mt-1 text-zinc-500 sm:hidden dark:text-zinc-400">{where}</div>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-zinc-500 sm:table-cell dark:text-zinc-400">
                    {where}
                  </TableCell>
                  <ActionCell ctx={ctx} itemKey={key} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

/**
 * Overdue (A7Y-5873: Today had no Overdue section while the record held 104 open
 * items dated before today). With the record read: every open item due before
 * today (`rosterOverdue`, This week's Overdue), less a row Critical already lists
 * and a row whose HELM proposal Inbox refused, its count in the header and the
 * list behind "Show the list". Without the record: the run's overdue and stale
 * rows, counted, and where they are listed.
 */
function OverdueSection({ ctx }: { ctx: CardContext }) {
  const [open, setOpen] = useState(false);
  if (!ctx.record) {
    const keys = [...ctx.facts.overdue, ...ctx.facts.stale].filter(
      (key) => !ctx.suppressedKeys.has(key)
    );
    return (
      <HelmSection title="Overdue" count={keys.length} lineOn="none">
        <HelmEmpty>
          {keys.length === 0 ? 'None' : 'Listed on the Inbox tab, under Overdue and Stale.'}
        </HelmEmpty>
      </HelmSection>
    );
  }
  const record = ctx.record;
  const criticalIds = new Set(
    ctx.facts.critical
      .map((c) => commitmentFor(ctx, c.key))
      .filter((c): c is HelmCommitment => c !== undefined && !ctx.suppressedKeys.has(c.key))
      .map((c) => c.evidence.ref.id)
  );
  const rows = rosterOverdue(record.items, record.today).filter(
    (item) => !criticalIds.has(item.id) && !ctx.refusedIds.has(item.id)
  );
  return (
    <HelmSection title="Overdue" count={rows.length} lineOn="none">
      {rows.length === 0 ? (
        <HelmEmpty>None</HelmEmpty>
      ) : (
        <>
          <button
            type="button"
            data-testid="helm-overdue-toggle"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="min-h-11 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {open ? 'Hide the list' : `Show the list (${rows.length})`}
          </button>
          {open ? (
            <Table dense className={HELM_TABLE_GUTTER} data-testid="helm-overdue-list">
              <TableHead>
                <TableRow>
                  <TableHeader className="w-24">Was due</TableHeader>
                  <TableHeader>Item</TableHeader>
                  <TableHeader className="hidden sm:table-cell">Table · block</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((item) => {
                  const where = [tableLabel(TABLE_BY_KIND[item.kind]), item.block]
                    .filter(Boolean)
                    .join(' · ');
                  const href = item.accountId ? accountPagePath(item.accountId) : HELM_WEEK_PATH;
                  return (
                    <TableRow key={item.id} data-testid="helm-overdue-row">
                      <TableCell className="tabular-nums text-zinc-500 dark:text-zinc-400">
                        {shortDate(rosterDue(item), record.today)}
                      </TableCell>
                      <TableCell className="whitespace-normal sm:min-w-56">
                        <LiveRowText item={item} href={href} />
                        <div className="mt-1 text-zinc-500 sm:hidden dark:text-zinc-400">
                          {where}
                        </div>
                      </TableCell>
                      <TableCell className="hidden text-zinc-500 sm:table-cell dark:text-zinc-400">
                        {where}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}
        </>
      )}
    </HelmSection>
  );
}

function ShippedSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const { title, rest } = splitLabel(group.label!.text);
  const rows = visibleRows(group.rows, ctx);
  return (
    <HelmSection
      title={title}
      count={group.rows.length > 0 ? rows.length : undefined}
      summary={group.rows.length > 0 && rest ? rest : undefined}
      lineOn={group.rows.length > 0 ? 'header' : 'section'}
    >
      {group.rows.length === 0 ? (
        <HelmEmpty>{rest || 'None'}</HelmEmpty>
      ) : rows.length === 0 ? null : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Ticket</TableHeader>
              <TableHeader>Title</TableHeader>
              <TableHeader>Status</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line, i) => {
              const key = line.keys[0] ?? null;
              const parsed = parseShipped(line.text);
              return (
                <TableRow key={`${key ?? 'line'}-${i}`} data-testid="helm-card-line">
                  {parsed ? (
                    <>
                      <TableCell className="font-medium">
                        <EvidenceLink ctx={ctx} keys={line.keys}>
                          {parsed.id}
                        </EvidenceLink>
                      </TableCell>
                      <TableCell className="whitespace-normal sm:min-w-48">
                        {parsed.title}
                      </TableCell>
                      <TableCell>
                        <span className="flex flex-wrap gap-1">
                          {parsed.statuses.map((status) => (
                            <Badge
                              key={status}
                              color={status.startsWith('carried since ') ? 'zinc' : 'amber'}
                            >
                              {status}
                            </Badge>
                          ))}
                        </span>
                      </TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={3} className="whitespace-normal">
                      {withoutBullet(line.text)}
                    </TableCell>
                  )}
                  <ActionCell ctx={ctx} itemKey={key} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

function MeetingsSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const label = group.label!;
  const { title, rest } = splitLabel(label.text);
  const events = label.keys.map((key) => ctx.facts.meetings.find((m) => m.key === key));
  const resolved = events.every(Boolean);
  const visible = events
    .filter((m): m is NonNullable<typeof m> => Boolean(m) && !ctx.suppressedKeys.has(m!.key))
    .sort((a, b) => (a.allDay === b.allDay ? (a.start < b.start ? -1 : 1) : a.allDay ? -1 : 1));

  // Every event hidden by an action: the section itself goes, as the whole
  // line used to.
  if (label.keys.length > 0 && resolved && visible.length === 0) return null;

  return (
    <HelmSection
      title={title}
      count={label.keys.length > 0 && resolved ? visible.length : undefined}
      lineOn={label.keys.length > 0 && resolved ? 'header' : 'section'}
    >
      {label.keys.length === 0 || !resolved ? (
        <HelmEmpty>{rest || 'None'}</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader className="w-20">Time</TableHeader>
              <TableHeader>Meeting</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((m) => (
              <TableRow key={m.key} data-testid="helm-card-meeting">
                <TableCell className="tabular-nums text-zinc-500 dark:text-zinc-400">
                  {m.allDay ? 'All day' : MADRID_TIME.format(new Date(m.start))}
                </TableCell>
                <TableCell className="whitespace-normal">
                  <EvidenceLink ctx={ctx} keys={[m.key]}>
                    {m.title}
                  </EvidenceLink>
                  {(m.clashesWith ?? []).length > 0 && (
                    <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                      {(m.clashesWith ?? []).map((other) => (
                        <Badge key={other} color="amber" data-testid="helm-meeting-clash">
                          clashes with {other}
                        </Badge>
                      ))}
                    </span>
                  )}
                  {(m.mergedKeys ?? []).length > 0 && (
                    <span
                      data-testid="helm-meeting-merged"
                      className="ml-2 text-xs text-zinc-500 dark:text-zinc-400"
                    >
                      also on your personal calendar
                    </span>
                  )}
                </TableCell>
                <ActionCell ctx={ctx} itemKey={m.key} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

function StatList({ items }: { items: { term: string; value: string }[] }) {
  return (
    <DescriptionList>
      {items.map((item) => (
        <div key={item.term} className="contents">
          <DescriptionTerm>{item.term}</DescriptionTerm>
          <DescriptionDetails>{item.value}</DescriptionDetails>
        </div>
      ))}
    </DescriptionList>
  );
}

function InboxSection({ group }: { group: CardGroup }) {
  const { title, rest } = splitLabel(group.label!.text);
  // cards.cjs writes `<Gmail part> · <LinkedIn part>`, Gmail first, always. The
  // Gmail part never contains ` · ` but the LinkedIn part does (`… message
  // notices · … invitation notices · LinkedIn says N pending`), so the line
  // splits once, at the first ` · `.
  const at = rest.indexOf(' · ');
  const items =
    at < 0
      ? null
      : [
          { term: 'Gmail', value: rest.slice(0, at).replace(/^Gmail /, '') },
          { term: 'LinkedIn', value: rest.slice(at + 3).replace(/^LinkedIn /, '') },
        ];
  return (
    <HelmSection title={title} lineOn="section">
      {items ? <StatList items={items} /> : <HelmEmpty>{rest}</HelmEmpty>}
    </HelmSection>
  );
}

/**
 * The judgment queue's numbers, and what they count (A7Y-5873: the page said
 * "Waiting 5" and the morning DM "22 waiting", and which was right was not
 * known): the admitted tickets by name, and how many are assigned in all.
 */
function JudgmentSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const { title, rest } = splitLabel(group.label!.text);
  const queue = ctx.facts.judgment_queue;
  const m = rest.match(/^(\d+) waiting · oldest (\d+) days · (\d+) new since (\S+)$/);
  const items = m
    ? [
        { term: 'Waiting', value: m[1] },
        { term: 'Oldest', value: `${m[2]} days` },
        { term: `New since ${m[4]}`, value: m[3] },
      ]
    : null;
  const of =
    typeof queue.assignedCount === 'number' ? ` of the ${queue.assignedCount} assigned to you` : '';
  return (
    <HelmSection title={title} lineOn="section">
      {items ? <StatList items={items} /> : <HelmEmpty>{rest}</HelmEmpty>}
      {items ? (
        <p
          data-testid="helm-judgment-counts"
          className="mt-3 text-sm/6 text-zinc-500 dark:text-zinc-400"
        >
          Counts the {queue.items.length}
          {of} in {HELM_JUDGMENT_STATE_LIST} that touch a live customer, change customer-facing
          wording or set a methodology threshold
          {queue.items.length > 0 ? ': ' : '.'}
          {queue.items.map((item, i) => (
            <span key={item.key}>
              {i > 0 ? ', ' : ''}
              <HelmLink
                href={item.url}
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                {item.identifier}
              </HelmLink>
            </span>
          ))}
          {queue.items.length > 0 ? '.' : ''}
        </p>
      ) : null}
    </HelmSection>
  );
}

const MADRID_DAY_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Madrid',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** `Tue 08:02` — the same Madrid day + clock cards.cjs prints on a Slack line. */
function madridDayTime(iso: string): string {
  const parts = Object.fromEntries(
    MADRID_DAY_TIME.formatToParts(new Date(iso)).map((p) => [p.type, p.value])
  );
  return `${parts.weekday} ${parts.hour}:${parts.minute}`;
}

type SlackRowData = {
  who: string;
  where: string;
  message: string;
  when: string;
  key: string | null;
};

function SlackRow({ row, ctx, testId }: { row: SlackRowData; ctx: CardContext; testId: string }) {
  const keys = row.key ? [row.key] : [];
  return (
    <TableRow data-testid={testId}>
      <TableCell className="hidden font-medium sm:table-cell">{row.who}</TableCell>
      <TableCell className="hidden text-zinc-500 sm:table-cell dark:text-zinc-400">
        {row.where}
      </TableCell>
      <TableCell className="whitespace-normal sm:min-w-56">
        <div className="text-zinc-500 sm:hidden dark:text-zinc-400">
          <span className="font-medium text-zinc-950 dark:text-white">{row.who}</span> · {row.where}{' '}
          · {row.when}
        </div>
        <EvidenceLink ctx={ctx} keys={keys}>
          <span className="line-clamp-3">{row.message}</span>
        </EvidenceLink>
      </TableCell>
      <TableCell className="hidden tabular-nums text-zinc-500 sm:table-cell dark:text-zinc-400">
        {row.when}
      </TableCell>
      <ActionCell ctx={ctx} itemKey={row.key} />
    </TableRow>
  );
}

function SlackSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const [expanded, setExpanded] = useState(false);
  const { title, rest } = splitLabel(group.label!.text);
  const rows = visibleRows(group.rows, ctx);

  // cards.cjs caps the @-mention lines and writes "+N more" for the rest;
  // those messages are in facts.inbox, so "+N more" expands to them here.
  const onCard = new Set(group.rows.flatMap((line) => line.keys));
  const more: SlackRowData[] = ctx.facts.inbox
    // An answered message and a burst follower (`burstCount` 0, folded into its
    // lead's "(+N more)") are not open rows of their own.
    .filter(
      (i) =>
        i.source === 'slack' &&
        !i.answered &&
        (i.burstCount ?? 1) >= 1 &&
        !onCard.has(i.key) &&
        !ctx.suppressedKeys.has(i.key)
    )
    .sort((a, b) => (a.receivedAt < b.receivedAt ? -1 : 1))
    .map((i) => ({
      who: i.sender.name ?? `name unavailable (${i.sender.address})`,
      where:
        i.slackChannel?.kind === 'im'
          ? 'DM'
          : i.slackChannel?.kind === 'mpim'
            ? 'MPIM'
            : `#${String(i.slackChannel?.label ?? '').replace(/^#/, '')}`,
      message: helmSlackText(i.snippet),
      when: madridDayTime(i.receivedAt),
      key: i.key,
    }));

  return (
    <HelmSection
      title={title}
      summary={group.rows.length > 0 ? rest : undefined}
      lineOn={group.rows.length > 0 ? 'header' : 'section'}
    >
      {group.rows.length === 0 ? (
        <HelmEmpty>{rest || 'None'}</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader className="hidden sm:table-cell">From</TableHeader>
              <TableHeader className="hidden sm:table-cell">Where</TableHeader>
              <TableHeader>Message</TableHeader>
              <TableHeader className="hidden sm:table-cell">When</TableHeader>
              <ActionHeader ctx={ctx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((line, i) => {
              const key = line.keys[0] ?? null;
              const parsed = parseSlack(line.text);
              if (!parsed) {
                // e.g. `· +2 more` — expands to the messages the card left off.
                const text = withoutBullet(line.text);
                return (
                  <TableRow key={`${key ?? 'line'}-${i}`} data-testid="helm-card-line">
                    <TableCell colSpan={4} className="text-zinc-500 dark:text-zinc-400">
                      {more.length > 0 && /^\+\d+ more$/.test(text) ? (
                        <button
                          type="button"
                          data-testid="helm-slack-more"
                          onClick={() => setExpanded((v) => !v)}
                          className="min-h-11 font-medium text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {expanded ? 'Show less' : text}
                        </button>
                      ) : (
                        text
                      )}
                    </TableCell>
                    {ctx.onAction && <TableCell />}
                  </TableRow>
                );
              }
              // The card line carries the first eight words; the page shows the
              // message's own snippet from facts, clamped to three lines (TPF).
              const snippet = ctx.facts.inbox.find((item) => item.key === key)?.snippet;
              return (
                <SlackRow
                  key={`${key ?? 'line'}-${i}`}
                  row={{ ...parsed, message: helmSlackText(snippet || parsed.message), key }}
                  ctx={ctx}
                  testId="helm-card-line"
                />
              );
            })}
            {expanded &&
              more.map((row) => (
                <SlackRow key={row.key} row={row} ctx={ctx} testId="helm-slack-more-row" />
              ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

/** The model's own key-tagged body lines — free text, so no column split. */
function NotesSection({ group, ctx }: { group: CardGroup; ctx: CardContext }) {
  const rows = visibleRows(group.rows, ctx);
  if (rows.length === 0) return null;
  return (
    <HelmSection title="Notes" count={rows.length} lineOn="none">
      <Table dense className={HELM_TABLE_GUTTER}>
        <TableHead>
          <TableRow>
            <TableHeader className="hidden w-12 sm:table-cell">Type</TableHeader>
            <TableHeader>Note</TableHeader>
            <ActionHeader ctx={ctx} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((line, i) => {
            const key = line.keys[0] ?? null;
            return (
              <TableRow key={`${key ?? 'line'}-${i}`} data-testid="helm-card-line">
                <TableCell className="hidden sm:table-cell">
                  <TypeLabel itemKey={key} />
                </TableCell>
                <TableCell className="whitespace-normal sm:min-w-56">
                  <div className="mb-1 sm:hidden">
                    <TypeLabel itemKey={key} />
                  </div>
                  <EvidenceLink ctx={ctx} keys={line.keys}>
                    {line.text}
                  </EvidenceLink>
                </TableCell>
                <ActionCell ctx={ctx} itemKey={key} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </HelmSection>
  );
}

function UnverifiedNotice({ group }: { group: CardGroup }) {
  return (
    <div
      data-testid="helm-card-line"
      className="flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-sm/6 text-amber-900 ring-1 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/20"
    >
      <AlertTriangle aria-hidden="true" className="mt-1 size-4 shrink-0" />
      <span>{group.label!.text}</span>
    </div>
  );
}

/** Any section cards.cjs adds later: heading + its lines as plain rows. */
function GenericSection({ group }: { group: CardGroup }) {
  const { title, rest } = splitLabel(group.label!.text);
  return (
    <HelmSection
      title={title}
      summary={rest || undefined}
      lineOn={group.rows.length > 0 ? 'header' : 'section'}
    >
      {group.rows.length > 0 && (
        <div className="divide-y divide-zinc-950/5 dark:divide-white/5">
          {group.rows.map((line, i) => (
            <div key={i} data-testid="helm-card-line" className="py-2 text-sm/6">
              {withoutBullet(line.text)}
            </div>
          ))}
        </div>
      )}
    </HelmSection>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────────── */

export function TodayCard({
  card,
  facts,
  suppressedKeys,
  onAction,
  record = null,
}: {
  card: HelmCard;
  facts: HelmFacts;
  suppressedKeys: ReadonlySet<string>;
  onAction?: (input: HelmActionInput) => void;
  /** The record now (A7Y-5492); null shows the run's own rows. */
  record?: HelmLiveRecord | null;
}) {
  const ctx: CardContext = {
    facts,
    linkIndex: buildHelmLinkIndex(facts),
    decisionIndex: onAction ? buildHelmDecisionIndex(facts) : null,
    suppressedKeys,
    refusedIds: refusedRosterIds(facts.proposals),
    onAction,
    record: record
      ? {
          today: record.today,
          items: record.items,
          openById: new Map(record.items.filter((i) => i.status === 'open').map((i) => [i.id, i])),
        }
      : null,
  };

  const groups = groupCardLines(card.lines);
  const bySection = (section: Section) => groups.filter((g) => g.section === section);
  const render = (group: CardGroup, i: number): ReactNode => {
    const key = `${group.section}-${i}`;
    switch (group.section) {
      case 'header':
        return <CardTitle key={key} line={group.label!} />;
      case 'critical':
        return <CriticalSection key={key} group={group} ctx={ctx} />;
      case 'due':
        return (
          <div key={key} className="space-y-6">
            <DueSection group={group} ctx={ctx} />
            <OverdueSection ctx={ctx} />
          </div>
        );
      case 'shipped':
        return <ShippedSection key={key} group={group} ctx={ctx} />;
      case 'meetings':
        return <MeetingsSection key={key} group={group} ctx={ctx} />;
      case 'inbox':
        return <InboxSection key={key} group={group} />;
      case 'judgment':
        return <JudgmentSection key={key} group={group} ctx={ctx} />;
      case 'slack':
        return <SlackSection key={key} group={group} ctx={ctx} />;
      case 'body':
        return <NotesSection key={key} group={group} ctx={ctx} />;
      case 'unverified':
        return <UnverifiedNotice key={key} group={group} />;
      default:
        return <GenericSection key={key} group={group} />;
    }
  };

  // Paired sections sit side by side when the card is wide enough — a
  // container query, so the half-width comparison copy stacks them instead.
  const PAIRS: [Section, Section][] = [
    ['shipped', 'meetings'],
    ['inbox', 'judgment'],
  ];
  const paired = new Set<Section>(PAIRS.flat());
  const out: ReactNode[] = [];
  groups.forEach((group, i) => {
    const pair = PAIRS.find(([first]) => first === group.section);
    if (pair) {
      const partner = bySection(pair[1])[0];
      out.push(
        <div key={`pair-${i}`} className="grid gap-6 @3xl:grid-cols-2">
          {render(group, i)}
          {partner && render(partner, groups.indexOf(partner))}
        </div>
      );
      return;
    }
    // A partner already rendered beside its pair's first section.
    if (
      paired.has(group.section) &&
      PAIRS.some(([first, second]) => second === group.section && bySection(first).length > 0)
    ) {
      return;
    }
    out.push(render(group, i));
  });

  return <div className="@container space-y-6">{out}</div>;
}
